import { useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import fallbackImg from "../../../../public/images/fallback.jpg";
import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Currency,
  EventBySlugQuery,
  EventSupportCurrencyProvider,
  GetAlbumDetailsQuery,
  GetForeignAccountDetailsQuery,
  GetWishListItemsQuery,
  GetWishListItemsQueryVariables,
} from "@src/@types/api.d";
import { GetWishListItems } from "@src/graphql/queries";
import Dialog from "@src/components/Dialog";
import Item from "@src/components/modals/ItemModal";
import Icon from "@src/components/Icon";
import getSymbolFromCurrency from "currency-symbol-map";
import ForeignDetails from "@src/components/modals/CashGiftModal/modals/ForeignDetails";
import { AvailablePaymentCurrencies } from "@src/components/BirthdayTemplate1/AsoEbiLink";

type Props = {
  albumDetailsData: GetAlbumDetailsQuery;
  eventId: number;
  eventUuid: string | undefined;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  foreignDetails:
    | GetForeignAccountDetailsQuery["getForeignAccountDetails"]
    | null
    | undefined;
  availableGiftPaymentCurrencies: AvailablePaymentCurrencies;
};

export type WishlistItemType = NonNullable<
  GetWishListItemsQuery["getWishListItems"]
>[number];

const Wishlist = ({
  albumDetailsData,
  eventId,
  eventUuid,
  setOpen,
  foreignDetails,
  availableGiftPaymentCurrencies,
}: Props) => {
  const [randomUrl, setRandomUrl] = useState<string | null>(null);
  const [openItem, setOpenItem] = useState<boolean>(false);
  const [openForeignDetails, setOpenForeignDetails] = useState(false);

  const [provider, setProvider] = useState<EventSupportCurrencyProvider | null>(
    null
  );
  const [selectedItem, setSelectedItem] = useState<WishlistItemType | null>(
    null
  );
  const [selectedForeignItem, setSelectedForeignItem] = useState<any>(null);

  const [foreignDetail, setForeignDetail] = useState<
    GetForeignAccountDetailsQuery["getForeignAccountDetails"][number] | null
  >(null);

  // Query for wishlist items
  const { data: getWishListItemsData, loading: getWishListItemsLoading } =
    useQuery<GetWishListItemsQuery, GetWishListItemsQueryVariables>(
      GetWishListItems,
      {
        skip: !eventUuid,
        variables: {
          eventId: `${eventUuid}`,
          pageSize: 50,
        },
      }
    );

  const mediaData = useMemo(
    () => albumDetailsData?.getMedia || [],
    [albumDetailsData]
  );

  const isValidImageUrl = (img: any): boolean =>
    img &&
    img.meta &&
    typeof img.meta.s3_url === "string" &&
    img.meta.s3_url.trim() !== "" &&
    img.meta.s3_url.startsWith("http");

  useEffect(() => {
    const validImages = mediaData.filter(isValidImageUrl);
    if (validImages.length > 0) {
      const random =
        validImages[Math.floor(Math.random() * validImages.length)];
      if (random && random.meta && random.meta.s3_url) {
        setRandomUrl(random.meta.s3_url);
      } else {
        setRandomUrl(
          typeof fallbackImg === "string" ? fallbackImg : fallbackImg.src
        );
      }
    } else {
      setRandomUrl(
        typeof fallbackImg === "string" ? fallbackImg : fallbackImg.src
      );
    }
  }, [mediaData]);

  const handleSendMoney = (item: WishlistItemType) => {
    // Find all entries for this currency in availableGiftPaymentCurrencies
    const currencyConfigs =
      availableGiftPaymentCurrencies?.filter(
        (entry) => entry?.currency === item?.currency
      ) || [];

    // Check if any entry has PAYSTACK provider
    const hasPaystack = currencyConfigs.some(
      (config) => config?.provider === EventSupportCurrencyProvider.Paystack
    );

    // Check if any entry has FLUTTERWAVE provider
    const hasFlutterwave = currencyConfigs.some(
      (config) => config?.provider === EventSupportCurrencyProvider.Flutterwave
    );

    if (hasPaystack) {
      setProvider(EventSupportCurrencyProvider.Paystack);
      setSelectedItem(item);
      setOpenItem(true);
    } else if (hasFlutterwave) {
      setProvider(EventSupportCurrencyProvider.Flutterwave);
      setSelectedItem(item);
      setOpenItem(true);
    } else {
      setForeignDetail(
        foreignDetails?.find((detail) => detail?.currency === item?.currency) ??
          null
      );
      setSelectedForeignItem(item);
      setOpenForeignDetails(true);
    }
  };

  if (
    !getWishListItemsLoading &&
    (!getWishListItemsData?.getWishListItems ||
      getWishListItemsData?.getWishListItems.length === 0)
  ) {
    return null;
  }

  return (
    <div
      className="bg-[#EAE4CC] h-screen flex items-center justify-center"
      style={{
        backgroundImage: randomUrl ? `url('${randomUrl}')` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Big Screen */}
      <div className="hidden sm:flex justify-center items-center w-[70%] h-[80%] bg-white rounded-2xl">
        <div className="flex-1 flex flex-col justify-center items-center h-full border-r border-black">
          <motion.h3
            className="font-pacifico text-black text-4xl lg:text-8xl italic font-light text-center tracking-wide leading-[1.4] relative z-10"
            style={{
              lineHeight: "140px",
            }}
            animate={{
              textShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
              transition: { duration: 0.3 },
            }}
          >
            Birthday Wishlist
          </motion.h3>
          <motion.p className="font-brooklyn tracking-wide text-lg text-center">
            WHO SAYS THE PERFECT GIFT DOESN'T EXIST?
          </motion.p>
        </div>
        <div className="flex-1 h-full p-6 overflow-hidden">
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
              {getWishListItemsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-lg text-gray-600">Loading...</div>
                </div>
              ) : (
                <div className="flex flex-col justify-center h-full gap-4">
                  {getWishListItemsData?.getWishListItems?.map(
                    (item, index: number) => (
                      <motion.div
                        key={`item-list-${index}`}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-brooklyn tracking-wide uppercase text-gray-900 mb-1">
                              {item?.name ?? ""}
                            </p>
                            <h2 className="text-lg font-bold text-black">
                              {!!item && !!item?.amount
                                ? `${getSymbolFromCurrency(
                                    item?.currency as Currency
                                  )} ${(item?.amount).toLocaleString()}`
                                : "Not available"}
                            </h2>
                          </div>

                          <div className="flex items-center gap-3">
                            {!!item?.link && item.link !== "not set" && (
                              <motion.a
                                target="_blank"
                                rel="noreferrer noopener"
                                href={item.link}
                                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <Icon iconName="link" />
                              </motion.a>
                            )}

                            <motion.button
                              className=" border-black border hover:bg-black text-black hover:text-white px-4 py-2 rounded-lg font-medium transition-colors"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() =>
                                handleSendMoney(item as WishlistItemType)
                              }
                            >
                              Send Money
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <Dialog open={openItem} onOpenChange={setOpenItem}>
          <Item
            title={selectedItem?.name ?? ""}
            id={selectedItem?.id ?? ""}
            amount={selectedItem?.amount ?? 0}
            eventId={eventId}
            eventUuid={eventUuid}
            setOpen={setOpen}
            setOpenItem={setOpenItem}
            currencyToPay={selectedItem?.currency as Currency}
            provider={provider}
          />
        </Dialog>
      </div>
      {/* Mobile */}
      <div className="sm:hidden flex flex-col w-[90%] py-2 bg-white rounded-2xl overflow-hidden">
        {/* Header Section */}
        <div className="flex flex-col justify-center items-center p-6 pb-4">
          <motion.h1
            className="font-pacifico text-black text-3xl italic font-light text-center tracking-wide leading-tight mb-3"
            animate={{
              textShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
              transition: { duration: 0.3 },
            }}
          >
            Birthday Wishlist
          </motion.h1>
          <motion.p className="font-brooklyn tracking-wide text-sm text-center text-gray-700">
            WHO SAYS THE PERFECT GIFT DOESN'T EXIST?
          </motion.p>
        </div>

        {/* Items Section */}
        <div className="flex-1 p-4 pt-0 overflow-hidden">
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
              {getWishListItemsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-lg text-gray-600">Loading...</div>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {getWishListItemsData?.getWishListItems?.map(
                    (item, index: number) => (
                      <motion.div
                        key={`item-list-mobile-${index}`}
                        className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <p className="font-brooklyn tracking-wide uppercase text-gray-900 mb-1 text-sm">
                              {item?.name ?? ""}
                            </p>
                            <h2 className="text-base font-bold text-black">
                              {!!item && !!item?.amount
                                ? `${getSymbolFromCurrency(
                                    item?.currency as Currency
                                  )} ${(item?.amount).toLocaleString()}`
                                : "Not available"}
                            </h2>
                          </div>

                          <div className="flex items-center justify-between">
                            {!!item?.link && item.link !== "not set" && (
                              <motion.a
                                target="_blank"
                                rel="noreferrer noopener"
                                href={item.link}
                                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <Icon iconName="link" />
                              </motion.a>
                            )}
                            <motion.button
                              className="border-black border hover:bg-black text-black hover:text-white px-3 py-2 rounded-lg font-medium transition-colors text-sm"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() =>
                                handleSendMoney(item as WishlistItemType)
                              }
                            >
                              Send Money
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Dialog open={openItem} onOpenChange={setOpenItem}>
        <Item
          title={selectedItem?.name ?? ""}
          id={selectedItem?.id ?? ""}
          amount={selectedItem?.amount ?? 0}
          eventId={eventId}
          eventUuid={eventUuid}
          setOpen={setOpen}
          setOpenItem={setOpenItem}
          currencyToPay={selectedItem?.currency as Currency}
          provider={provider}
        />
      </Dialog>
      {/* Foreign Details Modal */}
      <Dialog
        open={openForeignDetails}
        onOpenChange={setOpenForeignDetails}
        asChild
      >
        <ForeignDetails
          foreignDetail={foreignDetail}
          availableGiftPaymentCurrencies={availableGiftPaymentCurrencies}
          eventId={eventId}
          eventUuid={`${eventUuid}`}
          setOpen={setOpen}
          initialAmount={selectedForeignItem?.amount}
          itemName={selectedForeignItem?.name}
          foreignCurrency={selectedForeignItem?.currency as Currency}
        />
      </Dialog>
      ;
    </div>
  );
};

export default Wishlist;
